/**
 * THE INSTALL PROMPT — caught at boot, offered from the account menu.
 *
 * Chrome fires `beforeinstallprompt` when it decides the page is installable, and the event is the
 * ONLY handle on the install dialog: it cannot be constructed, re-requested, or fired on demand.
 * Not calling `preventDefault()` on it lets Chrome show its own mini-infobar and discards our
 * chance to offer the install anywhere else.
 *
 * ## Why this is module scope and not a hook
 *
 * The event lands EARLY — routinely before React has mounted, since Chrome fires it as soon as the
 * document and manifest are parsed. A listener registered inside a `useEffect` therefore misses it
 * on most cold loads, and the failure looks exactly like "the browser isn't firing it": no error,
 * no prompt, nothing to debug. {@see startInstallPromptCapture} is called from `app.tsx` at module
 * scope for that reason, and the React side reads what it caught rather than listening itself.
 *
 * That is also why this is a hand-rolled external store rather than a Jotai atom like
 * `useNotificationStore`. An atom's value lives in a store React owns; this value can arrive before
 * there is a React. `useSyncExternalStore` exists for exactly this shape.
 *
 * ## Not scoped to a tenant
 *
 * The `<link rel="manifest">` is tenant-only (see `inertia.blade.php`), so the central domain is
 * not installable and Chrome never fires the event there. Capturing unconditionally costs one
 * listener that never hears anything, where a guard would be a second copy of a rule the document
 * head already enforces.
 */

/**
 * Not in TypeScript's DOM lib. `beforeinstallprompt` is a Chromium extension to the platform rather
 * than a standard, so the shape is declared here — locally, not merged into the global `Event` —
 * for the same reason a cast belongs where an untyped value enters: widening what every listener in
 * the app is allowed to assume, to type the two lines that need it, is the wrong trade.
 */
export type BeforeInstallPromptEvent = Event & {
    readonly platforms: readonly string[];
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let hintIsOpen = false;
let listening = false;

const subscribers = new Set<() => void>();

function announce(): void {
    for (const notify of subscribers) notify();
}

/**
 * Registers the listeners. Idempotent — a second call is a no-op rather than a second listener,
 * so importing this from more than one entry point cannot double-announce.
 */
export function startInstallPromptCapture(): void {
    if (listening || typeof window === "undefined") return;

    listening = true;

    window.addEventListener("beforeinstallprompt", (event: Event) => {
        // Suppresses Chrome's own mini-infobar. Without it the browser owns the moment and this
        // event is spent, so the account menu would offer an install that cannot be performed.
        event.preventDefault();
        deferredPrompt = event as BeforeInstallPromptEvent;
        announce();
    });

    // Fires however the install happened — our button, or the browser's own address-bar affordance.
    // Both must retire the menu entry, which is why this listens for the outcome rather than
    // clearing inside `promptToInstall()` alone.
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
        hintIsOpen = false;
        announce();
    });
}

export function subscribeToInstallPrompt(onStoreChange: () => void): () => void {
    subscribers.add(onStoreChange);

    return () => {
        subscribers.delete(onStoreChange);
    };
}

/**
 * A boolean rather than the event itself, because `useSyncExternalStore` compares snapshots by
 * identity and re-reads on every render — handing out the event object is fine today and becomes an
 * infinite loop the moment this returns anything derived.
 */
export function installPromptIsAvailable(): boolean {
    return deferredPrompt !== null;
}

export function installHintIsOpen(): boolean {
    return hintIsOpen;
}

export function openInstallHint(): void {
    hintIsOpen = true;
    announce();
}

export function closeInstallHint(): void {
    hintIsOpen = false;
    announce();
}

/**
 * Shows the browser's install dialog. Must be called synchronously from a user gesture — Chrome
 * rejects a `prompt()` that arrives after an await.
 *
 * **The event is single-use.** Once prompted it cannot be prompted again, whatever the outcome, so
 * it is cleared BEFORE the dialog opens rather than after the choice comes back: clearing after
 * would leave a live-looking entry during the seconds the dialog is up, and a second click would
 * call `prompt()` on a spent event. A dismissal therefore removes the menu entry too, which is
 * correct — Chrome fires `beforeinstallprompt` again on a later visit, and the entry returns with
 * it.
 */
export async function promptToInstall(): Promise<
    "accepted" | "dismissed" | "unavailable"
> {
    const event = deferredPrompt;

    if (event === null) return "unavailable";

    deferredPrompt = null;
    announce();

    await event.prompt();

    const { outcome } = await event.userChoice;

    return outcome;
}

/**
 * Clears the captured state between tests.
 *
 * Module state outlives a test case, so without this the second test in a file inherits the first
 * one's prompt. It deliberately does NOT clear `listening`: the window and its listeners outlive
 * the reset too, and re-arming would register a duplicate pair that announces twice.
 */
export function resetInstallPromptForTests(): void {
    deferredPrompt = null;
    hintIsOpen = false;
}
